import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import FleetCarDialog, { FLEET_CARS } from "../components/FleetCarDialog";
import OnRequestFleet from "../components/OnRequestFleet";
import SEO, { LOCAL_BUSINESS_SCHEMA } from "../components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function HomePage() {
  const [selectedCar, setSelectedCar] = useState(null);
  const { t } = useTranslation();
  return (
    <div data-testid="home-page">
      <SEO
        titleKey="seo.home_title"
        descriptionKey="seo.home_description"
        path="/"
        schema={LOCAL_BUSINESS_SCHEMA}
      />
      <FleetCarDialog
        car={selectedCar}
        open={!!selectedCar}
        onOpenChange={(o) => !o && setSelectedCar(null)}
      />

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ backgroundColor: "#FAF9F6" }}
      >
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/hwov4wed_P1000576.webp"
            alt="Morgan 4/4 Series II 1958 — ETERNA Motion Venice"
            className="w-full h-full object-cover"
            style={{ opacity: 0.28 }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-screen pt-32 pb-20">
            <div className="lg:col-span-6 xl:col-span-5">
              <motion.p
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("home.hero_eyebrow")}
              </motion.p>

              <motion.h1
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={1}
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-[#1A1B1A] mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
              >
                {t("home.hero_title_1")}
                <br />
                <span style={{ fontWeight: 600 }}>{t("home.hero_title_2")}</span>
                <br />
                {t("home.hero_title_3")}
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={2}
                className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed max-w-md mb-10"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                {t("home.hero_subtitle")}
              </motion.p>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={3}
                className="flex flex-wrap gap-4"
              >
                <Link to="/services#fleet" data-testid="hero-cta-collection">
                  <button className="btn-luxury">{t("home.hero_cta_primary")}</button>
                </Link>
                <Link to="/contact" data-testid="hero-cta-contact">
                  <button className="btn-luxury-outline">{t("home.hero_cta_secondary")}</button>
                </Link>
              </motion.div>
            </div>

            <div className="lg:col-span-6 xl:col-span-7 relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
              >
                <div className="img-hover-zoom">
                  <img
                    src="https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/70l8o4yf_P1000563.webp"
                    alt="Morgan 4/4 Series II 1958 — ETERNA Motion, atelier veneziano"
                    className="w-full h-[50vh] lg:h-[70vh] object-cover"
                    data-testid="hero-image"
                  />
                </div>
                <div
                  className="absolute -bottom-6 -left-6 lg:-left-12 bg-[#FAF9F6] p-6 shadow-[0_20px_40px_rgba(0,0,0,0.04)] border border-[#D8D4CC]/40"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <p className="text-3xl font-light text-[#1A1B1A]">{t("home.since")}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {t("home.location")}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Statement */}
      <section data-testid="brand-statement" className="py-24 lg:py-32" style={{ backgroundColor: "#FAF9F6" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="text-center"
          >
            <div className="luxury-divider mx-auto mb-10" />
            <p
              className="text-lg sm:text-xl lg:text-2xl leading-relaxed text-[#1A1B1A] mb-8 tracking-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
            >
              {t("home.brand_quote")}
            </p>
            <p
              className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("home.brand_p1")}
            </p>
            <p
              className="text-sm sm:text-base text-[#4A4A4A] leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("home.brand_p2")}
            </p>
            <div className="luxury-divider mx-auto mt-10" />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section
        data-testid="services-section"
        className="py-24 lg:py-32 grain-overlay"
        style={{ backgroundColor: "#F3F1EC" }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              className="lg:col-span-7"
            >
              <p
                className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("home.services_eyebrow")}
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A] max-w-xl mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
              >
                {t("home.services_title_1")} <span style={{ fontWeight: 600 }}>{t("home.services_title_2")}</span>
              </h2>
              <div className="space-y-5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("home.services_p1")}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("home.services_p2")}</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">{t("home.services_p3")}</p>
              </div>
              <Link to="/services" data-testid="services-cta" className="inline-block mt-8">
                <button className="btn-luxury flex items-center gap-3">
                  {t("home.services_cta")} <ArrowRight strokeWidth={1.25} size={16} />
                </button>
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="lg:col-span-5 img-hover-zoom"
            >
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_eterna-classic-fleet/artifacts/rizwtbh1_P1000584.webp"
                alt="Morgan 4/4 Series II 1958 — leather strap detail"
                className="w-full h-64 lg:h-[500px] object-cover"
                data-testid="services-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fleet Preview */}
      <section
        data-testid="fleet-section"
        className="py-24 lg:py-32"
        style={{ backgroundColor: "#FAF9F6" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="mb-16 lg:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <div>
              <p
                className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-4"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("home.fleet_eyebrow")}
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1B1A]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
              >
                {t("home.fleet_title_1")} <span style={{ fontWeight: 600 }}>{t("home.fleet_title_2")}</span>
              </h2>
              <p
                className="text-sm text-[#4A4A4A] leading-relaxed mt-4 max-w-xl"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                {t("home.fleet_subtitle")}
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {/* Hero car: Morgan 4/4 — full width */}
            {FLEET_CARS.slice(0, 1).map((car) => (
              <motion.div
                key={car.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                data-testid={`fleet-car-card-${car.id}`}
                className="group flex flex-col lg:col-span-12"
              >
                <button
                  type="button"
                  onClick={() => setSelectedCar(car)}
                  data-testid={`fleet-card-image-${car.id}`}
                  className="block w-full img-hover-zoom overflow-hidden relative text-left"
                >
                  <img
                    src={car.cover}
                    alt={t(`fleet.${car.i18nKey}.name`)}
                    className="w-full h-80 lg:h-[640px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1B1A]/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </button>

                <div className="pt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div>
                    <p
                      className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-2"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
                    >
                      {t(`fleet.${car.i18nKey}.year`)}
                    </p>
                    <h3
                      className="text-3xl lg:text-5xl tracking-tighter text-[#1A1B1A]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
                    >
                      {t(`fleet.${car.i18nKey}.name`)}
                    </h3>
                    <p
                      className="text-base lg:text-lg text-[#4A4A4A] mt-2 italic max-w-xl"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                    >
                      {t(`fleet.${car.i18nKey}.tagline`)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedCar(car)}
                    data-testid={`fleet-scoprila-${car.id}`}
                    className="btn-luxury flex items-center gap-3 self-start md:self-auto whitespace-nowrap"
                  >
                    {t("common.discover")} <ArrowRight strokeWidth={1.25} size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 text-center"
          >
            <Link
              to="/portfolio"
              data-testid="fleet-view-portfolio"
              className="text-xs uppercase tracking-[0.15em] text-[#4A4A4A] hover:text-[#C4A462] inline-flex items-center gap-2 transition-colors duration-300"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("home.fleet_view_portfolio")} <ArrowRight strokeWidth={1.25} size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* On Request — Partner fleet */}
      <OnRequestFleet background="#F3F1EC" />

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-24 lg:py-32 grain-overlay"
        style={{ backgroundColor: "#1A1B1A" }}
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p
              className="text-xs uppercase tracking-[0.2em] text-[#C4A462] mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
              {t("home.cta_eyebrow")}
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#FAF9F6] mb-6 max-w-2xl mx-auto"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              {t("home.cta_title_1")} <span style={{ fontWeight: 600 }}>{t("home.cta_title_2")}</span>
            </h2>
            <p
              className="text-sm text-[#757575] leading-relaxed mb-10 max-w-lg mx-auto"
              style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
            >
              {t("home.cta_subtitle")}
            </p>
            <Link to="/contact" data-testid="cta-contact-btn">
              <button
                className="bg-[#C4A462] text-[#FAF9F6] px-10 py-4 text-xs uppercase tracking-[0.15em] hover:bg-[#A98A4E] transition-colors duration-400"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
              >
                {t("home.cta_button")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
